import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import PageHeader from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import { fmtINR, fmtDate, today } from "@/lib/format";
import { Upload, CheckCircle2, XCircle, RefreshCw, Link2 } from "lucide-react";
import TDSReport from "@/pages/TDSReport";
import TDSReconcile26AS from "@/pages/TDSReconcile26AS";

const RATES = [];

export default function TDSPage() {
  const [challans, setChallans] = useState([]);
  const [entries, setEntries] = useState([]);
  const [sections, setSections] = useState([]);
  const [openNewCh, setOpenNewCh] = useState(false);
  const [form, setForm] = useState({ challan_number: "", bsr_code: "", date: today(), section: "194J", amount: 0 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setChallans((await api.get("/tds/challans")).data);
    setEntries((await api.get("/tds/26as")).data);
    setSections((await api.get("/tds/sections")).data);
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!form.challan_number || !form.amount) return toast.error("Number & amount required");
    try { await api.post("/tds/challans", form); toast.success("Challan added"); setOpenNewCh(false); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const autoMap = async (challanId) => {
    try {
      const r = await api.post(`/tds/challans/${challanId}/auto-map`, {});
      if (r.data.ok) {
        toast.success(`Mapped ${r.data.bills_mapped.length} bill(s) · settled ${fmtINR(r.data.settled)}${r.data.unallocated > 0 ? ` · unallocated ${fmtINR(r.data.unallocated)}` : ""}`);
        load();
      } else toast.error("Mapping failed");
    } catch (e) { toast.error(e?.response?.data?.detail || "Auto-map failed"); }
  };

  // Live 201(1A) interest preview when user edits challan drawer
  const [preview, setPreview] = useState({ interest: 0, late_bills: 0, unallocated: 0 });
  useEffect(() => {
    if (!openNewCh || !form.section || !form.date || !form.amount) { setPreview({ interest: 0, late_bills: 0, unallocated: 0 }); return; }
    const t = setTimeout(async () => {
      try {
        const r = await api.post("/tds/challans/interest-preview", { section: form.section, date: form.date, amount: Number(form.amount) });
        setPreview(r.data || { interest: 0, late_bills: 0, unallocated: 0 });
      } catch { setPreview({ interest: 0, late_bills: 0, unallocated: 0 }); }
    }, 400);
    return () => clearTimeout(t);
  }, [openNewCh, form.section, form.date, form.amount]);

  const upload26as = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const r = await api.post("/tds/26as/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.imported} entries`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const runReco = async () => {
    setBusy(true);
    try { const r = await api.post("/tds/26as/reconcile"); toast.success(`Matched ${r.data.matched} of ${r.data.total}`); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  const matched = entries.filter((e) => e.status === "matched").length;

  return (
    <div className="p-6 lg:p-8 space-y-6" data-testid="tds-page">
      <PageHeader eyebrow="Compliance" title="TDS Management" description="TDS challans, section-wise tracking and Form 26AS reconciliation." />

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Challans deposited</div><div className="metric-value text-2xl mt-2">{challans.length}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Total deposited</div><div className="metric-value text-2xl mt-2">{fmtINR(challans.reduce((s, c) => s + c.amount, 0))}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">26AS entries</div><div className="metric-value text-2xl mt-2">{entries.length}</div></div>
        <div className="card-sharp p-5"><div className="text-[10px] font-mono uppercase text-muted-foreground">Matched</div><div className="metric-value text-2xl mt-2 text-emerald-600">{matched}</div></div>
      </div>

      <Tabs defaultValue="challans">
        <TabsList className="rounded-sm">
          <TabsTrigger value="challans" className="rounded-sm">Challans</TabsTrigger>
          <TabsTrigger value="tds-payable" className="rounded-sm" data-testid="tab-tds-payable">TDS Payable Report</TabsTrigger>
          <TabsTrigger value="tds-receivable" className="rounded-sm" data-testid="tab-tds-receivable">TDS Receivable Report</TabsTrigger>
          <TabsTrigger value="26as" className="rounded-sm">26AS Reconciliation</TabsTrigger>
        </TabsList>

        <TabsContent value="challans" className="mt-4 space-y-3">
          <div className="flex justify-end"><Button onClick={() => setOpenNewCh(true)} className="rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="new-challan-btn">+ Challan</Button></div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Date</th><th>Challan #</th><th>BSR</th><th>Section</th><th className="text-right">Amount</th><th>Bills mapped</th><th>Unallocated</th><th /></tr></thead>
              <tbody>
                {challans.length === 0 && <tr><td colSpan="8" className="text-center py-8 text-muted-foreground">No challans yet</td></tr>}
                {challans.map((c) => (
                  <tr key={c.id} data-testid={`challan-${c.challan_number}`}>
                    <td className="text-xs">{fmtDate(c.date)}</td>
                    <td className="font-mono text-xs">{c.challan_number}</td>
                    <td className="font-mono text-xs">{c.bsr_code || "—"}</td>
                    <td className="text-xs">{c.section}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(c.amount)}</td>
                    <td className="text-xs">
                      {c.mapped_bills?.length > 0 ? (
                        <span className="text-emerald-600" title={c.mapped_bills.map((b) => b.bill_number).join(", ")}>
                          {c.mapped_bills.length} bill{c.mapped_bills.length !== 1 ? "s" : ""} · settled
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Not mapped</span>
                      )}
                    </td>
                    <td className="text-right tabular-nums text-xs text-amber-600">
                      {c.unallocated_amount > 0 ? fmtINR(c.unallocated_amount) : "—"}
                    </td>
                    <td className="text-right">
                      {!c.auto_mapped_at && (
                        <Button size="sm" variant="outline" onClick={() => autoMap(c.id)} className="rounded-sm gap-1 h-7 text-xs"
                                 data-testid={`automap-${c.challan_number}`}>
                          <Link2 className="w-3 h-3" /> Auto-map
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="tds-payable" className="mt-4">
          <TDSReport kind="payable" />
        </TabsContent>

        <TabsContent value="tds-receivable" className="mt-4 space-y-4">
          <TDSReport kind="receivable" />
          <TDSReconcile26AS />
        </TabsContent>

        <TabsContent value="26as" className="mt-4 space-y-3">
          <div className="card-sharp p-4 flex flex-wrap items-center gap-3">
            <label className="cursor-pointer inline-flex items-center gap-2 h-9 px-3 border rounded-sm bg-card hover:bg-muted text-sm" data-testid="upload-26as">
              <Upload className="w-3.5 h-3.5" /> {busy ? "Working…" : "Upload 26AS CSV"}
              <input type="file" accept=".csv,.txt" className="hidden" onChange={(e) => e.target.files?.[0] && upload26as(e.target.files[0])} disabled={busy} />
            </label>
            <Button onClick={runReco} variant="outline" className="rounded-sm gap-1.5" data-testid="run-reco"><RefreshCw className="w-3.5 h-3.5" /> Run auto-match</Button>
            <div className="text-xs text-muted-foreground">Expected CSV columns: <code className="font-mono">deductor_tan, deductor_name, section, date, amount_credited, tds_deducted</code></div>
          </div>
          <div className="card-sharp overflow-hidden">
            <table className="w-full dense">
              <thead><tr><th>Deductor</th><th>TAN</th><th>Section</th><th>Date</th><th className="text-right">Amt Credited</th><th className="text-right">TDS</th><th>Status</th></tr></thead>
              <tbody>
                {entries.length === 0 && <tr><td colSpan="7" className="text-center py-8 text-muted-foreground">Upload a 26AS CSV to start reconciling</td></tr>}
                {entries.map((e) => (
                  <tr key={e.id}>
                    <td className="text-sm">{e.deductor_name}</td>
                    <td className="font-mono text-xs">{e.deductor_tan}</td>
                    <td className="text-xs">{e.section}</td>
                    <td className="text-xs">{e.date}</td>
                    <td className="text-right tabular-nums">{fmtINR(e.amount_credited)}</td>
                    <td className="text-right tabular-nums font-medium">{fmtINR(e.tds_deducted)}</td>
                    <td>{e.status === "matched" ? <span className="inline-flex items-center gap-1 text-emerald-600 text-[10px] font-mono uppercase"><CheckCircle2 className="w-3 h-3" /> Matched</span> : <span className="inline-flex items-center gap-1 text-warning text-[10px] font-mono uppercase"><XCircle className="w-3 h-3" /> Unmatched</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Sheet open={openNewCh} onOpenChange={setOpenNewCh}>
        <SheetContent side="right" className="w-full sm:max-w-md rounded-none space-y-3">
          <SheetHeader><SheetTitle className="font-heading">New TDS Challan</SheetTitle></SheetHeader>
          <div className="space-y-3">
            <div><Label>Challan Number</Label><Input value={form.challan_number} onChange={(e) => setForm({ ...form, challan_number: e.target.value })} className="rounded-sm mt-1 font-mono" data-testid="challan-no" /></div>
            <div><Label>BSR Code</Label><Input value={form.bsr_code} onChange={(e) => setForm({ ...form, bsr_code: e.target.value })} className="rounded-sm mt-1 font-mono" placeholder="7-digit BSR" /></div>
            <div><Label>Section</Label>
              <Select value={form.section} onValueChange={(v) => setForm({ ...form, section: v })}>
                <SelectTrigger className="rounded-sm mt-1" data-testid="challan-section"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72"><div className="max-h-72 overflow-y-auto">
                  {sections.map((r) => <SelectItem key={r.section} value={r.section}>{r.section} — {r.description} ({r.rate}%)</SelectItem>)}
                </div></SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="rounded-sm mt-1" /></div>
            <div><Label>Amount deposited</Label><Input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: Number(e.target.value) })} className="rounded-sm mt-1 tabular-nums" data-testid="challan-amount" /></div>
            {preview.late_bills > 0 && (
              <div className="border border-amber-300 bg-amber-50 rounded-sm p-3 text-xs space-y-1" data-testid="interest-preview">
                <div className="font-medium text-amber-800">⚠ Section 201(1A) interest will be auto-posted</div>
                <div className="text-amber-700">
                  <b>{preview.late_bills}</b> bill(s) are past the 7-of-next-month due date. Interest at 1.5% / month = <b>{fmtINR(preview.interest)}</b>.
                </div>
                {preview.on_time_bills > 0 && (
                  <div className="text-emerald-700">{preview.on_time_bills} bill(s) still on time (no interest).</div>
                )}
                {preview.unallocated > 0.01 && (
                  <div className="text-amber-700">Unallocated (shortfall): {fmtINR(preview.unallocated)}</div>
                )}
              </div>
            )}
            {preview.late_bills === 0 && preview.on_time_bills > 0 && (
              <div className="border border-emerald-300 bg-emerald-50 rounded-sm p-2 text-xs text-emerald-700">
                ✓ On-time challan — {preview.on_time_bills} bill(s) will be settled with no interest.
              </div>
            )}
          </div>
          <Button onClick={create} className="w-full rounded-sm bg-accent hover:bg-accent/90 text-white" data-testid="challan-save">Save challan</Button>
        </SheetContent>
      </Sheet>
    </div>
  );
}
